<template>
  <div
    v-if="FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO"
    class="mb-12 flex flex-wrap gap-8"
  >
    <div>Content cards: {{ contentCards.length }}</div>
    <div>isRequestingContentCards (Braze): {{ isRequestingContentCards }}</div>
    <div>isLoading: {{ isLoading }}</div>
  </div>

  <BlockTitle :title />

  <div
    ref="tabs-scroller"
    class="mb-20 mt-4 flex grow flex-col justify-between overflow-scroll scrollbar-none"
    :class="maskClass"
  >
    <div
      :aria-label="title || ''"
      class="flex w-max items-start gap-8 overflow-x-auto overflow-y-hidden scrollbar-none"
      role="tablist"
    >
      <template v-if="isRequestingContentCards || isLoading">
        <RevSkeleton height="40px" shape="rectangle" width="130px" />
        <RevSkeleton height="40px" shape="rectangle" width="130px" />
      </template>

      <RevChip
        v-for="(tab, tabIndex) in contentCards"
        v-else
        :id="`tab-${tab.id}`"
        :key="tab.id"
        ref="tabItem"
        :aria-controls="`panel-${tab.id}`"
        :aria-selected="activeTabIndex === tabIndex"
        class="shrink-0"
        :label="tab.title"
        :model-value="activeTabIndex === tabIndex"
        role="tab"
        :tabindex="activeTabIndex === tabIndex ? 0 : -1"
        @click="() => onTabClick(tabIndex)"
        @keydown.left.prevent="() => onTabClick(tabIndex - 1)"
        @keydown.right.prevent="() => onTabClick(tabIndex + 1)"
      />
    </div>
  </div>

  <RevSkeleton
    v-if="isRequestingContentCards || isLoading"
    height="362px"
    shape="rectangle"
    width="100%"
  />

  <div
    v-for="(card, cardIndex) in contentCards"
    v-show="activeTabIndex === cardIndex && !isLoading"
    :id="`panel-${card.id}`"
    :key="`panel-${card.id}`"
    :aria-hidden="activeTabIndex !== cardIndex"
    :aria-labelledby="`tab-${card.id}`"
    role="tabpanel"
  >
    <component
      :is="card.tag"
      v-bind="card.props"
      :data-test="card.id"
      @error="handleCardError(card)"
      @loaded="handleCardLoaded"
    />
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import { getBrazeInstance } from '@backmarket/nuxt-module-tracking/getBrazeInstance'
import { RevChip } from '@ds/components/Chip'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'

import {
  type ContentCard,
  transformContentCards,
} from './PersonalizedFeed.utils'

export type PersonalizedFeedBlockProps = PersonalizedFeedApiProps & BlockProps

const { feed } = defineProps<PersonalizedFeedBlockProps>()
const { FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO = false } =
  useRuntimeConfig().public

const braze = getBrazeInstance()
let subscriptionId: string | undefined

const isRequestingContentCards = ref(true)
const isLoading = ref(true)

const activeTabIndex = ref(0)
const tabItem = useTemplateRef('tabItem')
const scrollerRef = useTemplateRef<HTMLDivElement>('tabs-scroller')
const { maskClass, onScrollAreaChange } = useScrollMaskClass(scrollerRef)

const contentCards = ref<ContentCard[]>([])

onMounted(() => {
  braze?.requestContentCardsRefresh()

  subscriptionId = braze?.subscribeToContentCardsUpdates(({ cards }) => {
    contentCards.value = transformContentCards({ cards, feed })

    isRequestingContentCards.value = false

    if (contentCards.value.length === 0) {
      isLoading.value = false
    }
  })
})

onBeforeUnmount(() => {
  if (subscriptionId) {
    braze?.removeSubscription(subscriptionId)
  }
})

function onTabClick(i: number) {
  let index = i
  if (index < 0) index = contentCards.value.length - 1
  if (index >= contentCards.value.length) index = 0

  tabItem.value?.[index]?.$el.focus()

  activeTabIndex.value = index
}

function handleCardLoaded() {
  isLoading.value = false
  isRequestingContentCards.value = false
  onScrollAreaChange()
}

function handleCardError(card: ContentCard) {
  handleCardLoaded()

  contentCards.value = contentCards.value.filter(
    (contentCard) => contentCard.id !== card.id,
  )
}
</script>
